/**
 * Add people to a grupo.
 *
 * It uses `POST /{group}/members/{user}` PER PERSON rather than the
 * bulk `PUT /members`, and that is a decision, not laziness: the PUT
 * REPLACES the membership with exactly the list it is given, so a caller
 * holding a stale list silently removes whoever joined since. The
 * endpoint reports those removals in `removed` precisely because they
 * are a real hazard — and the honest way to avoid them is not to send a
 * whole list from a dialog whose only job is to ADD.
 *
 * Adding somebody who is already a member is a 200 with no row and no
 * audit entry: the PK is `(group_id, user_id)` and the request's intent
 * is already true. So an already-member simply does not appear in the
 * picker.
 *
 * `note` from the response is rendered verbatim by the caller: a
 * membership change can mint a new effective scope whose FILE does not
 * exist, which is a 503 for that person until something builds it.
 */

import { Button, Input, Modal } from "@omnitok/ui";
import { Callout, EmptyState } from "@pervasivemind/admin-ui";
import { Spinner } from "@pervasivemind/bi-components";
import { Check, Plus, UserX } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useT } from "@/i18n";
import { groupsApi, type GroupMemberOut } from "@/lib/access-api";
import { adminUsersApi, type UserAdmin } from "@/lib/admin-users-api";

/** Adds in order and STOPS at the first refusal. The ids that went
 *  through are returned with the refusal instead of being hidden
 *  behind it: those people already have the group's access. */
export async function addInOrder(
  ids: readonly string[],
  addOne: (id: string) => Promise<{ note: string }>,
): Promise<{ added: string[]; note: string; error: string | null }> {
  const added: string[] = [];
  let note = "";
  for (const id of ids) {
    try {
      note = (await addOne(id)).note;
    } catch (err) {
      return { added, note, error: err instanceof Error ? err.message : String(err) };
    }
    added.push(id);
  }
  return { added, note, error: null };
}

export function AddMembersModal({
  open,
  groupId,
  current,
  onClose,
  onAdded,
  onChanged,
}: {
  open: boolean;
  groupId: string;
  current: readonly GroupMemberOut[];
  onClose: () => void;
  onAdded: (note: string) => void;
  /** Some were added before a refusal: refresh, keep the modal open. */
  onChanged: (note: string) => void;
}): JSX.Element | null {
  const { t } = useT();
  const [candidates, setCandidates] = useState<UserAdmin[] | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setError(null);
    try {
      setCandidates(await adminUsersApi.all());
    } catch (err) {
      setCandidates(null);
      setError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setPicked(new Set());
    setSearch("");
    void load();
  }, [open, load]);

  const memberIds = useMemo(
    () => new Set(current.map((m) => m.user_id)),
    [current],
  );

  const offerable = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return (candidates ?? [])
      .filter((u) => !memberIds.has(u.id))
      .filter(
        (u) =>
          needle === "" ||
          u.name.toLowerCase().includes(needle) ||
          u.email.toLowerCase().includes(needle),
      );
  }, [candidates, memberIds, search]);

  if (!open) return null;

  const submit = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const out = await addInOrder([...picked], (id) => groupsApi.addMember(groupId, id));
      if (out.error === null) {
        onAdded(out.note);
        return;
      }
      // Partial application is reported, not hidden: whoever went
      // through already has access, so the caller refreshes NOW and the
      // picker stops offering them.
      setError(out.error);
      if (out.added.length > 0) {
        setPicked((prev) => {
          const next = new Set(prev);
          for (const id of out.added) next.delete(id);
          return next;
        });
        onChanged(out.note);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={() => (busy ? undefined : onClose())}
      title={t("access.groups.members.addTitle")}
      size="md"
      footer={
        <div className="flex justify-end gap-2">
          <Button ghost size="sm" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={picked.size === 0 || busy}
            onClick={() => void submit()}
          >
            {t("access.groups.members.addCount", { count: picked.size })}
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        {error !== null && (
          <Callout tone="danger" title={t("access.common.errorTitle")}>
            <span className="whitespace-pre-wrap">{error}</span>
          </Callout>
        )}

        <Input
          value={search}
          aria-label={t("access.groups.members.searchLabel")}
          placeholder={t("access.groups.members.searchLabel")}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setSearch(e.target.value)
          }
        />

        {candidates === null ? (
          <span className="inline-flex items-center gap-1.5 text-sm text-neutral-500">
            <Spinner size={14} ariaLabel={t("common.loading")} />
            {t("common.loading")}
          </span>
        ) : offerable.length === 0 ? (
          <EmptyState
            icon={<UserX size={20} />}
            title={t("access.groups.members.noneOfferable")}
            description={t("access.groups.members.noneOfferableDesc")}
          />
        ) : (
          <ul className="max-h-80 space-y-1 overflow-y-auto">
            {offerable.map((user) => {
              const on = picked.has(user.id);
              return (
                <li key={user.id}>
                  <button
                    type="button"
                    aria-pressed={on}
                    onClick={() =>
                      setPicked((prev) => {
                        const next = new Set(prev);
                        if (next.has(user.id)) next.delete(user.id);
                        else next.add(user.id);
                        return next;
                      })
                    }
                    className={
                      on
                        ? "flex w-full items-center gap-2 rounded-md border border-primary-light bg-primary-soft px-3 py-1.5 text-left text-sm font-semibold text-primary-ink"
                        : "flex w-full items-center gap-2 rounded-md border border-neutral-200 bg-white px-3 py-1.5 text-left text-sm text-neutral-700 hover:bg-neutral-50"
                    }
                  >
                    {on ? (
                      <Check size={14} strokeWidth={3} aria-hidden="true" />
                    ) : (
                      <Plus size={14} strokeWidth={3} aria-hidden="true" />
                    )}
                    <span className="min-w-0 flex-1 truncate">{user.name}</span>
                    <span className="truncate font-mono text-[13px] text-neutral-500">
                      {user.email}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        <p className="text-[13px] text-neutral-600">
          {t("access.groups.members.addNote")}
        </p>
      </div>
    </Modal>
  );
}
